import React, { useEffect, useMemo, useState } from 'react';
import EntitySearchSelect from '@/components/UI/EntitySearchSelect';
import EntityListSelect from '@/components/UI/EntityListSelect';
import { trpc } from '@/api/trpc';
import { useAppQuery } from '@/hooks/useAppQuery';
import { useEntityLists } from '@/hooks/useEntityLists';
import { DEFAULT_REQUISITIES_STALE_TIME } from '@/config/constants';
import type { EntityTypeUnion } from '@einhaus/project314-types';

export interface EntityConfig {
    entityId: number;
    entityType: EntityTypeUnion['entityType'];
    symbol: string;
}

/** Universes above this size collapse to a summary — a few hundred rows buries the rest of the form. */
const ENTITY_PREVIEW_LIMIT = 20;

const entityKey = (entityId: number, entityType: string) => `${entityType}:${entityId}`;

interface EntitySelectionPanelProps {
    selectedEntities: EntityConfig[];
    onEntitiesChange: (entities: EntityConfig[]) => void;
    /**
     * Entity list the current selection was resolved from, or null for a hand-picked universe.
     * Controlled by the parent so callers can persist the provenance alongside the resolved
     * entities — the entities stay authoritative, this only records where they came from.
     */
    entityListId: number | null;
    onEntityListIdChange: (entityListId: number | null) => void;
    error?: string;
    className?: string;
}

const EntitySelectionPanel: React.FC<EntitySelectionPanelProps> = ({
    selectedEntities,
    onEntitiesChange,
    entityListId,
    onEntityListIdChange,
    error,
    className = ''
}) => {
    // A selected list implies list mode, so hydrating an existing selection lands on the right tab
    // without an extra sync effect. The manual toggle only decides the mode while no list is set.
    const [manualMode, setManualMode] = useState<'individual' | 'list'>('individual');
    const entitySelectionMode = entityListId !== null ? 'list' : manualMode;

    // Set only when the user picks a list, so hydrating a stored selection never re-resolves (and
    // silently rewrites) a universe that was snapshotted when the list had different members.
    const [pendingListResolveId, setPendingListResolveId] = useState<number | null>(null);
    const [detachedListWarning, setDetachedListWarning] = useState<string | null>(null);
    const [showAllEntities, setShowAllEntities] = useState(false);

    const { entityLists, query: entityListsQuery } = useEntityLists({ enabled: entitySelectionMode === 'list' });

    // Only needed to attach symbols to a freshly picked list; a hydrated selection carries its own.
    const allEntitiesQuery = useAppQuery({
        ...trpc.entity.fetchAllEntities.queryOptions({}),
        enabled: pendingListResolveId !== null,
        staleTime: DEFAULT_REQUISITIES_STALE_TIME
    });

    const selectedEntityList = useMemo(
        () => (entityListId === null ? null : (entityLists.find((list) => list.id === entityListId) ?? null)),
        [entityLists, entityListId]
    );

    const handleEntityModeChange = (mode: 'individual' | 'list') => {
        setManualMode(mode);
        setPendingListResolveId(null);
        setDetachedListWarning(null);

        if (mode === 'individual') {
            // Detach from the list but keep what it resolved to: the universe is unchanged, it is
            // just hand-picked from here on.
            onEntityListIdChange(null);
            return;
        }

        onEntitiesChange([]);
        onEntityListIdChange(null);
    };

    const handleEntityListChange = (nextEntityListId: number | null) => {
        setDetachedListWarning(null);
        setPendingListResolveId(nextEntityListId);
        onEntityListIdChange(nextEntityListId);

        if (nextEntityListId === null) {
            onEntitiesChange([]);
        }
    };

    // Resolve a freshly picked list into entities with symbols.
    useEffect(() => {
        if (pendingListResolveId === null) return;

        const entityList = entityLists.find((list) => list.id === pendingListResolveId);
        const allEntities = allEntitiesQuery.data?.entities;

        if (!entityList || !allEntities) return;

        const entitiesByKey = new Map(allEntities.map((entity) => [entityKey(entity.id, entity.type), entity]));

        const resolvedEntities = entityList.entities
            .map((listEntity) => {
                const fullEntity = entitiesByKey.get(entityKey(listEntity.id, listEntity.type));

                if (!fullEntity) {
                    return null;
                }

                return {
                    entityId: listEntity.id,
                    entityType: listEntity.type,
                    symbol: fullEntity.symbol || fullEntity.name || `Entity-${listEntity.id}`
                };
            })
            .filter((entity): entity is EntityConfig => entity !== null);

        setPendingListResolveId(null);
        setShowAllEntities(false);
        onEntitiesChange(resolvedEntities);
    }, [pendingListResolveId, entityLists, allEntitiesQuery.data, onEntitiesChange]);

    // A list that no longer contains every selected entity cannot vouch for this universe, and the
    // backend rejects that pairing. Drop the stale link (keeping the entities) and say so, instead
    // of letting an unrelated edit fail on save.
    useEffect(() => {
        if (entityListId === null || pendingListResolveId !== null) return;
        if (!entityListsQuery.isSuccess) return;

        if (!selectedEntityList) {
            setDetachedListWarning(
                `Entity list #${entityListId} is no longer available, so these ${selectedEntities.length} entities are now hand-picked.`
            );
            onEntityListIdChange(null);
            return;
        }

        const listKeys = new Set(selectedEntityList.entities.map((entity) => entityKey(entity.id, entity.type)));
        const hasOutOfListEntities = selectedEntities.some((entity) => !listKeys.has(entityKey(entity.entityId, entity.entityType)));

        if (!hasOutOfListEntities) return;

        setDetachedListWarning(
            `Entity list "${selectedEntityList.name}" has changed since this selection was saved, so these ${selectedEntities.length} entities are now hand-picked. Re-select the list to use its current members.`
        );
        onEntityListIdChange(null);
    }, [entityListId, pendingListResolveId, entityListsQuery.isSuccess, selectedEntityList, selectedEntities, onEntityListIdChange]);

    const handleAddEntity = (
        entity: { entityId: number; entityType: EntityTypeUnion['entityType']; symbol?: string; name?: string } | null
    ) => {
        if (!entity) return;

        const exists = selectedEntities.some((e) => e.entityId === entity.entityId && e.entityType === entity.entityType);

        if (exists) return;

        onEntitiesChange([
            ...selectedEntities,
            {
                entityId: entity.entityId,
                entityType: entity.entityType,
                symbol: entity.symbol || entity.name || `Entity-${entity.entityId}`
            }
        ]);
    };

    const handleRemoveEntity = (entityId: number, entityType: EntityTypeUnion['entityType']) => {
        onEntitiesChange(selectedEntities.filter((e) => !(e.entityId === entityId && e.entityType === entityType)));
    };

    const isCollapsible = selectedEntities.length > ENTITY_PREVIEW_LIMIT;
    const visibleEntities = isCollapsible && !showAllEntities ? selectedEntities.slice(0, ENTITY_PREVIEW_LIMIT) : selectedEntities;

    const renderEntityRows = (options: { removable: boolean }) => (
        <div className="space-y-2">
            <div className="max-h-60 overflow-y-auto space-y-1">
                {visibleEntities.map((entity) => (
                    <div
                        className="flex items-center justify-between bg-zinc-900 border border-zinc-800 rounded px-3 py-2"
                        key={entityKey(entity.entityId, entity.entityType)}
                    >
                        <div>
                            <span className="text-white font-medium">{entity.symbol}</span>
                            <span className="text-zinc-500 text-sm ml-2">({entity.entityType})</span>
                        </div>
                        {options.removable && (
                            <button
                                className="text-red-400 hover:text-red-300 transition-colors"
                                type="button"
                                onClick={() => handleRemoveEntity(entity.entityId, entity.entityType)}
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path d="M6 18L18 6M6 6l12 12" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} />
                                </svg>
                            </button>
                        )}
                    </div>
                ))}
            </div>
            {isCollapsible && (
                <button
                    className="text-blue-400 hover:text-blue-300 text-sm transition-colors"
                    type="button"
                    onClick={() => setShowAllEntities((previous) => !previous)}
                >
                    {showAllEntities
                        ? 'Show fewer'
                        : `Show all ${selectedEntities.length} entities (${selectedEntities.length - ENTITY_PREVIEW_LIMIT} more)`}
                </button>
            )}
        </div>
    );

    return (
        <div className={className}>
            {error && <p className="text-red-400 text-sm mb-2">{error}</p>}

            {detachedListWarning && <p className="text-yellow-400 text-sm mb-3">{detachedListWarning}</p>}

            {/* Selection Mode Toggle */}
            <div className="mb-4">
                <div className="flex space-x-2 mb-4">
                    <button
                        className={`px-4 py-2 rounded text-sm font-medium transition-colors ${
                            entitySelectionMode === 'individual' ? 'bg-blue-600 text-white' : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                        }`}
                        type="button"
                        onClick={() => handleEntityModeChange('individual')}
                    >
                        Individual Entities
                    </button>
                    <button
                        className={`px-4 py-2 rounded text-sm font-medium transition-colors ${
                            entitySelectionMode === 'list' ? 'bg-blue-600 text-white' : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                        }`}
                        type="button"
                        onClick={() => handleEntityModeChange('list')}
                    >
                        Use Entity List
                    </button>
                </div>
            </div>

            {/* Individual Entity Selection */}
            {entitySelectionMode === 'individual' && (
                <div>
                    <div className="mb-4">
                        <EntitySearchSelect
                            label="Add Entity"
                            placeholder="Search and add entities..."
                            value={null}
                            onChange={handleAddEntity}
                        />
                    </div>

                    {selectedEntities.length > 0 && (
                        <div className="space-y-2">
                            <div className="text-zinc-400 text-sm mb-2">Selected Entities:</div>
                            {renderEntityRows({ removable: true })}
                        </div>
                    )}
                </div>
            )}

            {/* Entity List Selection */}
            {entitySelectionMode === 'list' && (
                <div>
                    <div className="mb-4">
                        <EntityListSelect
                            label="Select Entity List"
                            placeholder="-- Select an Entity List --"
                            value={entityListId}
                            onChange={handleEntityListChange}
                        />
                    </div>

                    {pendingListResolveId !== null && <div className="text-zinc-400 text-sm">Loading entity details...</div>}

                    {entityListId !== null && pendingListResolveId === null && selectedEntities.length > 0 && (
                        <div className="space-y-2">
                            <div className="text-zinc-400 text-sm mb-2">
                                {selectedEntityList
                                    ? `${selectedEntityList.name} — ${selectedEntities.length} ${selectedEntities.length === 1 ? 'entity' : 'entities'}`
                                    : `${selectedEntities.length} ${selectedEntities.length === 1 ? 'entity' : 'entities'} from the selected list`}
                            </div>
                            {renderEntityRows({ removable: false })}
                        </div>
                    )}

                    {entityListId !== null &&
                        pendingListResolveId === null &&
                        selectedEntities.length === 0 &&
                        allEntitiesQuery.data !== undefined && (
                            <div className="text-yellow-400 text-sm">
                                This entity list appears to be empty or contains invalid entities.
                            </div>
                        )}
                </div>
            )}

            <div className="mt-3 text-zinc-400 text-sm">
                {selectedEntities.length} {selectedEntities.length === 1 ? 'entity' : 'entities'} selected
                {selectedEntityList && <span className="ml-2">(from list: {selectedEntityList.name})</span>}
            </div>
        </div>
    );
};

export default EntitySelectionPanel;
